import React, { useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Brain, Users, TrendingUp, Zap, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { AuthContext } from '../App';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LandingPage = () => {
  const navigate = useNavigate();
  const { setUser } = useContext(AuthContext);

  useEffect(() => {
    // Check for session_id in URL fragment
    const hash = window.location.hash;
    if (hash.includes('session_id=')) {
      const sessionId = hash.split('session_id=')[1].split('&')[0];
      handleSessionId(sessionId);
    }
  }, []);

  const handleSessionId = async (sessionId) => {
    console.log('Received session_id:', sessionId);
    try {
      const response = await axios.post(
        `${API}/auth/session`,
        { session_id: sessionId },
        { withCredentials: true }
      );
      console.log('Auth response:', response.data);
      setUser(response.data.user);
      window.history.replaceState(null, '', '/dashboard');
      navigate('/dashboard');
    } catch (error) {
      console.error('Session creation error:', error);
      console.error('Error response:', error.response?.data);
      toast.error('Authentication failed. Please try again.');
      window.history.replaceState(null, '', '/');
    }
  };

  const handleLogin = () => {
    const redirectUrl = `${window.location.origin}/`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-cyan-50">
      {/* Navigation */}
      <nav className="border-b border-slate-200 bg-white/80 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Brain className="w-8 h-8 text-indigo-600" />
            <span className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-cyan-600 bg-clip-text text-transparent" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              IntentAI
            </span>
          </div>
          <Button
            onClick={handleLogin}
            data-testid="login-button"
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded-full font-medium"
          >
            Sign In
          </Button>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-center mb-16">
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-6 leading-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-cyan-600 bg-clip-text text-transparent">
              Predict Purchase Intent
            </span>
            <br />
            <span className="text-slate-800">in Seconds, Not Months</span>
          </h1>
          <p className="text-lg sm:text-xl text-slate-600 max-w-3xl mx-auto mb-10" style={{ fontFamily: 'Inter, sans-serif' }}>
            Revolutionary AI-powered consumer simulations that validate your product concepts instantly. 
            Run thousands of synthetic consumer tests with 90% accuracy.
          </p>
          <Button
            onClick={handleLogin}
            data-testid="get-started-button"
            size="lg"
            className="bg-gradient-to-r from-indigo-600 to-cyan-600 hover:from-indigo-700 hover:to-cyan-700 text-white px-10 py-6 text-lg rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
          >
            Get Started Free
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-3 gap-8 mt-20">
          <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300 border border-slate-100" data-testid="feature-instant-validation">
            <div className="w-14 h-14 bg-gradient-to-br from-indigo-100 to-indigo-200 rounded-xl flex items-center justify-center mb-6">
              <Zap className="w-7 h-7 text-indigo-600" />
            </div>
            <h3 className="text-2xl font-bold mb-4 text-slate-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Instant Validation</h3>
            <p className="text-slate-600 leading-relaxed">
              Upload your concept and get purchase intent scores in minutes. No waiting weeks for survey responses.
            </p>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300 border border-slate-100" data-testid="feature-ai-consumers">
            <div className="w-14 h-14 bg-gradient-to-br from-purple-100 to-purple-200 rounded-xl flex items-center justify-center mb-6">
              <Users className="w-7 h-7 text-purple-600" />
            </div>
            <h3 className="text-2xl font-bold mb-4 text-slate-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>AI Consumers</h3>
            <p className="text-slate-600 leading-relaxed">
              Simulate thousands of diverse consumer profiles with different demographics and psychographics.
            </p>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300 border border-slate-100" data-testid="feature-deep-insights">
            <div className="w-14 h-14 bg-gradient-to-br from-cyan-100 to-cyan-200 rounded-xl flex items-center justify-center mb-6">
              <TrendingUp className="w-7 h-7 text-cyan-600" />
            </div>
            <h3 className="text-2xl font-bold mb-4 text-slate-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Deep Insights</h3>
            <p className="text-slate-600 leading-relaxed">
              Get emotional drivers, market segments, objections, and demographic breakdowns for actionable intelligence.
            </p>
          </div>
        </div>

        {/* How It Works */}
        <div className="mt-32">
          <h2 className="text-4xl font-bold text-center mb-16 text-slate-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            How It Works
          </h2>
          <div className="grid md:grid-cols-3 gap-12">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white text-2xl font-bold">
                1
              </div>
              <h3 className="text-xl font-bold mb-3 text-slate-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Upload Your Concept</h3>
              <p className="text-slate-600">
                Add your product description, ad copy, or landing page design
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white text-2xl font-bold">
                2
              </div>
              <h3 className="text-xl font-bold mb-3 text-slate-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>AI Simulations Run</h3>
              <p className="text-slate-600">
                Advanced AI models simulate diverse consumer reactions
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white text-2xl font-bold">
                3
              </div>
              <h3 className="text-xl font-bold mb-3 text-slate-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Get Actionable Results</h3>
              <p className="text-slate-600">
                View purchase intent scores, key drivers, and market insights
              </p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-32 bg-gradient-to-r from-indigo-600 to-cyan-600 rounded-3xl p-12 text-center text-white">
          <h2 className="text-4xl font-bold mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Ready to Revolutionize Your Consumer Research?
          </h2>
          <p className="text-xl mb-8 text-indigo-50">
            Join forward-thinking marketers who validate ideas in seconds
          </p>
          <Button
            onClick={handleLogin}
            data-testid="cta-get-started-button"
            size="lg"
            className="bg-white text-indigo-600 hover:bg-indigo-50 px-10 py-6 text-lg rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
          >
            Start Testing Now
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;